import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, Plus, Search, X } from "lucide-react";

import {
  downloadAsset,
  listAssetTaxonomy,
  listAssets,
  listStarredAssets,
  starAsset,
  unstarAsset,
  type AssetCard as AssetCardType,
  type AssetListResponse,
  type FacetCount,
  type StarredAssetsResponse,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { AssetCard } from "@/components/assets/AssetCard";
import { AssetFilters, type AssetFilterState } from "@/components/assets/AssetFilters";
import { EmailClientDialog } from "@/components/assets/EmailClientDialog";
import { PreviewDialog } from "@/components/assets/PreviewDialog";
import { StarredDrawer } from "@/components/assets/StarredDrawer";
import { AssetAdminPanel } from "@/components/assets/admin/AssetAdminPanel";
import { AddResourceDrawer } from "@/components/assets/admin/AddResourceDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FEATURE_EMPTY_MESSAGE, FEATURE_LIBRARY_EMPTY_MESSAGE, hubCopy } from "@/lib/asset-labels";

const EMPTY_FILTERS: AssetFilterState = {
  audience: null,
  documentTypes: [],
  topicIds: [],
  industryIds: [],
};

export function AssetsHubRoute() {
  const { accessToken, data } = useCurrentUser();
  const isAdmin = data?.role === "admin";
  const hero = hubCopy(isAdmin);
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState<AssetFilterState>(EMPTY_FILTERS);
  const [previewAssetItem, setPreviewAssetItem] = useState<AssetCardType | null>(null);
  const [emailAssetItem, setEmailAssetItem] = useState<AssetCardType | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manageAssetId, setManageAssetId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // 250ms debounce so each keystroke doesn't refetch.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // CHP-431: resolve a selected topic's stable ID to its current slug for the
  // `topic=` query param. The source of truth is the taxonomy list, NOT the
  // filtered list's facets: this query's key is independent of the selection,
  // so there is exactly ONE id→slug mapping at a time. Sourcing the resolution
  // from the per-filter-key facet caches instead let two list-query keys hold
  // conflicting slugs for the same id after a rename round-trip, which flipped
  // the resolved slug → query key → cache on every render (a synchronous
  // ping-pong → React "max update depth"). A rename invalidates ["assets"]
  // (TaxonomyPicker), so this refetches and re-points the filter to the new
  // slug in-place.
  const topicsTax = useQuery<FacetCount[]>({
    queryKey: ["assets", "taxonomy", "topic", accessToken],
    queryFn: () => listAssetTaxonomy(accessToken!, "topic"),
    enabled: !!accessToken,
  });
  const topicSlugById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of topicsTax.data ?? []) if (t.id) m[t.id] = t.key;
    return m;
  }, [topicsTax.data]);

  // CHP-1035: industries carry the same ID-keyed selection + slug-resolved
  // query-param contract as topics (CHP-431 rationale above applies verbatim).
  const industriesTax = useQuery<FacetCount[]>({
    queryKey: ["assets", "taxonomy", "industry", accessToken],
    queryFn: () => listAssetTaxonomy(accessToken!, "industry"),
    enabled: !!accessToken,
  });
  const industrySlugById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of industriesTax.data ?? []) if (t.id) m[t.id] = t.key;
    return m;
  }, [industriesTax.data]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (debounced) p.set("q", debounced);
    if (filters.audience) p.set("audience", filters.audience);
    for (const dt of filters.documentTypes) p.append("document_type", dt);
    // The backend filters by slug; the selection is held as stable IDs. Resolve
    // each ID to its current slug — an unresolved ID (tag removed from every
    // asset) is omitted here and pruned by the reconcile effect; a renamed tag
    // resolves to its new slug, so the filter follows the rename (CHP-431).
    for (const id of filters.topicIds) {
      const slug = topicSlugById[id];
      if (slug) p.append("topic", slug);
    }
    for (const id of filters.industryIds) {
      const slug = industrySlugById[id];
      if (slug) p.append("industry", slug);
    }
    // Admins see drafts/archived too; the backend ignores this flag for
    // non-admins, so it's safe to send unconditionally — but we gate it
    // anyway to keep the query key stable for consumers.
    if (isAdmin) p.set("include_drafts", "true");
    return p;
  }, [debounced, filters, isAdmin, topicSlugById, industrySlugById]);

  const list = useQuery<AssetListResponse>({
    queryKey: ["assets", "list", accessToken, params.toString()],
    queryFn: () => listAssets(accessToken!, params),
    enabled: !!accessToken,
  });

  // CHP-431: silently auto-deselect a topic removed from every asset. Facets are
  // computed over the full visible set (independent of the current selection),
  // so a topic appears in `facets.topics` iff some visible asset still carries
  // it. Dropping it from the selection here avoids a stuck zero-result filter
  // with no visible pill to clear. A renamed tag is NOT dropped — its ID stays
  // present (the slug just changes), and resolution follows it via topicSlugById
  // above. Guarded: runs only once facets exist, and setFilters returns the
  // previous state unchanged when nothing is pruned, so it cannot loop.
  const facets = list.data?.facets;
  useEffect(() => {
    if (!facets) return;
    const validTopicIds = new Set(facets.topics.map((t) => t.id));
    const validIndustryIds = new Set(facets.industries.map((t) => t.id));
    setFilters((prev) => {
      const topics = prev.topicIds.filter((id) => validTopicIds.has(id));
      const industries = prev.industryIds.filter((id) => validIndustryIds.has(id));
      if (topics.length === prev.topicIds.length && industries.length === prev.industryIds.length) {
        return prev;
      }
      return { ...prev, topicIds: topics, industryIds: industries };
    });
  }, [facets]);

  // Drives the "View bookmarks (n)" button count; the drawer reuses this cache key.
  const starred = useQuery<StarredAssetsResponse>({
    queryKey: ["assets", "starred", accessToken],
    queryFn: () => listStarredAssets(accessToken!),
    enabled: !!accessToken,
  });

  const toggleStar = useMutation({
    mutationFn: (asset: AssetCardType) =>
      asset.starred ? unstarAsset(accessToken!, asset.id) : starAsset(accessToken!, asset.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assets"] }),
  });

  function handleDownload(asset: AssetCardType) {
    if (!accessToken) return;
    const ext = asset.format ? `.${asset.format.toLowerCase()}` : "";
    void downloadAsset(accessToken, asset.id, `${asset.title}${ext}`);
  }

  const items = list.data?.items ?? [];

  // Distinguish a truly empty library (no search/filter applied) from a filtered
  // zero-result, so a first-run admin sees an "add the first one" prompt rather
  // than the misleading "no resources match your filters" message (CHP-806).
  const hasActiveFilter =
    debounced.length > 0 ||
    filters.audience !== null ||
    filters.documentTypes.length > 0 ||
    filters.topicIds.length > 0 ||
    filters.industryIds.length > 0;

  const starredButton = (
    <Button variant="outline" onClick={() => setDrawerOpen(true)}>
      <Bookmark className="h-4 w-4" aria-hidden /> View bookmarks ({starred.data?.total ?? 0})
    </Button>
  );

  return (
    <div className="container py-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{hero.title}</h1>
          <p className="text-sm text-muted-foreground">{hero.subtitle}</p>
        </div>
        {/* CHP-272 standard view has no "Add new resource"; starred stands alone.
            Admins get it grouped next to "Add new resource" below (CHP-273). */}
        {!isAdmin ? starredButton : null}
      </div>

      {isAdmin ? (
        <div className="mb-4 flex flex-wrap gap-2">
          <Button onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden /> Add new resource
          </Button>
          {starredButton}
        </div>
      ) : null}

      <div className="mb-4 relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          ref={searchRef}
          className="pl-9 pr-9"
          placeholder="Type to search files instantly…"
          aria-label="Search resources"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search ? (
          <button
            type="button"
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => {
              setSearch("");
              searchRef.current?.focus();
            }}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>

      {list.data ? (
        <div className="mb-6">
          <AssetFilters
            facets={list.data.facets}
            total={list.data.total}
            state={filters}
            onChange={setFilters}
          />
        </div>
      ) : null}

      {list.isLoading ? (
        <p role="status">Loading resources…</p>
      ) : list.error ? (
        <p role="alert">Couldn't load resources.</p>
      ) : items.length === 0 ? (
        hasActiveFilter ? (
          <p className="text-sm text-muted-foreground">{FEATURE_EMPTY_MESSAGE}</p>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">{FEATURE_LIBRARY_EMPTY_MESSAGE}</p>
            {isAdmin ? (
              <Button onClick={() => setUploadOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden /> Add new resource
              </Button>
            ) : null}
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              onPreview={setPreviewAssetItem}
              onDownload={handleDownload}
              onEmail={setEmailAssetItem}
              onToggleStar={(a) => toggleStar.mutate(a)}
              onManage={isAdmin ? (a) => setManageAssetId(a.id) : undefined}
              busy={toggleStar.isPending}
            />
          ))}
        </div>
      )}

      <PreviewDialog
        asset={previewAssetItem}
        token={accessToken}
        onClose={() => setPreviewAssetItem(null)}
        onDownload={handleDownload}
      />
      <EmailClientDialog
        asset={emailAssetItem}
        token={accessToken}
        onClose={() => setEmailAssetItem(null)}
        onSent={() => undefined}
      />
      <StarredDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        token={accessToken}
        onView={(asset) => {
          setDrawerOpen(false);
          setPreviewAssetItem(asset);
        }}
        onUnstar={(asset) => toggleStar.mutate(asset)}
      />
      {isAdmin ? (
        <>
          <AddResourceDrawer
            open={uploadOpen}
            onOpenChange={setUploadOpen}
            token={accessToken}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ["assets"] })}
          />
          <AssetAdminPanel
            assetId={manageAssetId}
            token={accessToken}
            onOpenChange={(open) => !open && setManageAssetId(null)}
            onChanged={() => undefined}
          />
        </>
      ) : null}
    </div>
  );
}
